<template>
  <BasicBlock
    :title="$t('payment_method.block.title')"
    class="fapi-form-payment-methods"
  >
    <div
      class="f-p-3 f--mt-5 f-font-semibold"
      v-if="!formResource.gopay_connection_production_mode"
      v-text="$t('payment_method.test_connection')"
      style="color: #ffa830"
    ></div>
    <ValidationProvider
      rules="required"
      slim
      :skipIfEmpty="false"
      v-slot="{ validate, errors }"
    >
      <input
        type="hidden"
        name="paymentMethod"
        @change="validate"
        v-model="value"
      />

      <SimpleErrorLine
        v-if="errors.length > 0"
        :message="$t('payment_method.validations.required')"
      ></SimpleErrorLine>
    </ValidationProvider>

    <div>
      <div class="f-block f-w-full f-text-right f-mb-4 f--mt-4">
        <a
          href="#"
          class="f-text-blue-500 hover:f-underline"
          v-if="
            renderStripePaymentElement &&
            showStripePaymentElement &&
            paymentMethods.length > 1
          "
          @click.prevent="value = null"
        >
          {{ $t("payment_method.card_payment.another_method") }}
        </a>
      </div>

      <div
        :class="[
          'f-grid f-gap-2 sm:f-gap-4',
          variant === 'tile' ? 'f-grid-cols-2 sm:f-grid-cols-3' : '',
          variant === 'radio'
            ? singleColumn
              ? 'f-grid-cols-1'
              : 'sm:f-grid-cols-2'
            : '',
          showStripePaymentElement && renderStripePaymentElement
            ? 'f-hidden'
            : '',
        ]"
      >
        <PaymentMethod
          :class="[
            'fapi-form-payment-method',
            'fapi-form-payment-method-' + index,
          ]"
          v-for="(paymentMethod, index) in filteredPaymentMethods"
          :key="index"
          :paymentMethod="paymentMethod"
          :currency="currency"
          :country="country"
          :shippingCountry="shippingCountry"
          :index="index"
          :active="value !== null && value.index === index"
          :phoneRequiredOrFilled="phoneRequiredOrFilled"
          :orderPriceIncVat="orderPriceIncVat"
          :formResource="formResource"
          :variant="variant"
          :singleColumn="singleColumn"
          :paymentMethodItem="
            paymentMethod.payment_type === 'collect on delivery'
              ? collectOnDeliveryPaymentMethodItem
              : null
          "
          @paymentMethodChanged="paymentMethodChanged"
          @visiblePaymentMethod="visiblePaymentMethodList"
        />
      </div>
      <StripePaymentElement
        v-if="renderStripePaymentElement"
        v-show="showStripePaymentElement || !notStripeElementsPaymentMethods"
        :required="showStripePaymentElement"
        :amount="orderPriceIncVat"
        :currency="currency"
        :stripe="stripe"
        :formResource="formResource"
        :mode="stripeElementsMode"
        @stripeElements="onStripeElements"
        @paymentMethodSelected="onStripePaymentMethodSelected"
      />
    </div>
  </BasicBlock>
</template>
<script lang="ts">
import Vue, { PropType } from "vue";
import PaymentMethodData from "@/module/Form/Resources/PaymentMethodResource";
import PaymentMethod from "@/components/Form/PaymentMethods/PaymentMethod.vue";
import BasicBlock from "@/components/Form/BasicBlock.vue";
import FormResource from "@/module/Form/Resources/FormResource";
import TemplateSettings from "@/module/Form/Resources/TemplateSettings";
import {
  isObjectOrNull,
  isStringOrNull,
} from "@/module/ComponentPropsValidations/validations";
import { ValidationProvider } from "vee-validate";
import SimpleErrorLine from "@/components/Form/SimpleErrorLine.vue";
import FormPaymentMethodItem from "@/module/FormPaymentMethodItem";
import UrlParser from "@/module/Utils/UrlParser";
import StripePaymentElement from "@/components/Form/PaymentMethods/StripePaymentElement.vue";

interface ComponentData {
  list: Array<any> | null;
  value: {
    index: number;
    payment_type: string;
    bank: string | null;
    payment_method_item: FormPaymentMethodItem | null;
  } | null;
}

export default Vue.extend({
  name: "PaymentMethods",

  components: {
    StripePaymentElement,
    SimpleErrorLine,
    BasicBlock,
    PaymentMethod,
    ValidationProvider,
  },

  props: {
    paymentMethods: {
      type: Array as PropType<Array<PaymentMethodData>>,
      required: true,
    },
    templateSettings: {
      type: Object as PropType<TemplateSettings>,
      required: true,
    },
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    country: {
      required: true,
      validator: isStringOrNull,
    },
    shippingCountry: {
      required: true,
      validator: isStringOrNull,
    },
    phoneRequiredOrFilled: {
      type: Boolean,
      required: true,
    },
    orderPriceIncVat: {
      type: Number,
      required: true,
    },
    collectOnDeliveryPaymentMethodItem: {
      required: true,
      validator: isObjectOrNull,
    },
    stripe: {
      required: false,
    },
    isPhysicalItemSelected: {
      type: Boolean,
      required: true,
    },
  },

  data(): ComponentData {
    return {
      list: [],
      value: null,
    };
  },

  computed: {
    singleColumn(): boolean {
      return (
        this.templateSettings.container_settings.payment.layout.column ===
        "single"
      );
    },

    variant(): string {
      return (
        this.templateSettings.container_settings.payment.layout.variant ??
        "tile"
      );
    },

    renderStripePaymentElement(): boolean {
      if (this.orderPriceIncVat > 0) {
        return (
          this.country !== null &&
          (this.formResource.allow_new_upsell_process ||
            (!this.formResource.allow_upsells &&
              this.formResource.template_settings?.container_settings.payment
                .allow_inline_payment_card === true)) &&
          this.stripe !== null &&
          this.allowedStripePaymentElementPayments &&
          UrlParser.getInstance().isHttps()
        );
      } else {
        return (
          this.stripe !== null &&
          this.country !== null &&
          (this.formResource.allow_new_upsell_process ||
            (!this.formResource.allow_upsells &&
              this.formResource.template_settings?.container_settings.payment
                .allow_inline_payment_card === true)) &&
          this.allowedStripePaymentElementPayments &&
          this.formResource.allow_recurring &&
          this.formResource.allow_gopay_recurring_payments &&
          UrlParser.getInstance().isHttps()
        );
      }
    },

    allowedStripePaymentElementPayments(): boolean {
      const allowedPaymentTypes = [
        "gpay",
        "apple pay",
        "credit card",
        "paypal",
      ];
      return this.formResource.payment_methods.some((method) =>
        allowedPaymentTypes.includes(method.payment_type),
      );
    },
    showStripePaymentElement(): boolean {
      const paymentTypes = ["apple pay", "gpay", "credit card", "paypal"];
      return (
        this.value !== null && paymentTypes.includes(this.value.payment_type)
      );
    },

    filteredPaymentMethods(): PaymentMethodData[] {
      if (
        this.renderStripePaymentElement &&
        !this.notStripeElementsPaymentMethods
      ) {
        return [];
      }

      return this.paymentMethods.filter((method) => {
        if (
          method.payment_type === "collect on delivery" &&
          !this.isPhysicalItemSelected
        ) {
          return false;
        }
        return true;
      });
    },

    notStripeElementsPaymentMethods(): boolean {
      return (
        this.paymentMethods.length > 1 &&
        this.paymentMethods.some((method) =>
          ["cash", "wire", "collect on delivery"].includes(method.payment_type),
        )
      );
    },

    isOnlyStripePaymentElement(): boolean {
      return (
        this.renderStripePaymentElement &&
        this.filteredPaymentMethods.length === 0
      );
    },

    stripeElementsMode() {
      return this.orderPriceIncVat > 0 ? "payment" : "setup";
    },
  },

  methods: {
    visiblePaymentMethodList(data: any) {
      if (!this.list) {
        return;
      }

      this.list.push(data);
    },

    onStripeElements(elements: any) {
      this.$emit("stripeElements", elements);
    },

    paymentMethodChanged(
      value: {
        index: number;
        payment_type: string;
        bank: string | null;
        payment_method_item: FormPaymentMethodItem | null;
      } | null,
    ) {
      this.value = value;
      this.$emit("paymentMethodChanged", value);
    },

    onStripePaymentMethodSelected(paymentMethodType: string) {
      this.value = {
        index: -1,
        payment_type: paymentMethodType,
        bank: null,
        payment_method_item: null,
      };
      this.$emit("paymentMethodChanged", this.value);
    },
  },

  watch: {
    renderStripePaymentElement(newVal: boolean) {
      if (newVal) {
        this.value = {
          index: -1,
          payment_type: "credit card",
          bank: null,
          payment_method_item: null,
        };
        this.$emit("paymentMethodChanged", this.value);
      } else {
        this.value = null;
        this.$emit("paymentMethodChanged", null);
      }
    },
  },

  mounted(): void {
    if (this.list === null) {
      return;
    }

    if (this.isOnlyStripePaymentElement) {
      this.value = {
        index: -1,
        payment_type: "credit card",
        bank: null,
        payment_method_item: null,
      };
      this.$emit("paymentMethodChanged", this.value);
    } else {
      const item = this.list.shift();

      if (item !== undefined) {
        this.value = item;
        this.$emit("paymentMethodChanged", this.value);
      }
    }

    this.$nextTick(() => {
      const mountedEvent = new CustomEvent("fapiFormPaymentMounted");
      document.dispatchEvent(mountedEvent);
    });

    this.list = null;
  },
});
</script>
